import * as React from 'react';
import {
  useFormatDateByUserProfileLocation,
  NUMERIC_YEAR_MONTH_DAY,
} from '@leagueplatform/web-common';
import { XYChart, Tooltip, AxisBottom, AxisLeft, Grid } from '../xychart';
import { useGetLeftAxisLabelOffset } from '../bar-graph/utils/use-calculate-left-axis-label-offset';
import { RangeBar, RangeBarProps } from '../xychart/components/range-bar';
import { groupByKey } from './utils/group-by-key';
import { TooltipMarker } from './tooltip-marker';
import * as RangeGraph from './types/range-graph';

const NUM_Y_TICKS = 5;

export function MasonryRangeGraph<Datum>({
  data,
  accessors,
  title,
  description,
  axisLeftLabel,
  ariaDatumLabel,
  axisBottomLabel,
  renderTooltip,
  barPadding,
  getDatumSymbol,
  yAxisMax,
  xAxisTicks,
  xAxisLabelTicks,
  xAxisTickFormat,
  datumsPerColumn: datumsPerColumnProp,
}: RangeGraph.MasonryRangeGraph<Datum>) {
  const formatDateWithUserProfileLocation =
    useFormatDateByUserProfileLocation();
  const yAxisLabelOffset = useGetLeftAxisLabelOffset(yAxisMax);
  const [leftMargin, setLeftMargin] = React.useState(0);
  const [bottomMargin, setBottomMargin] = React.useState(0);

  // Group by date.
  const groupedByDate = groupByKey(
    (datum) =>
      formatDateWithUserProfileLocation(accessors.xAccessor(datum), {
        ...NUMERIC_YEAR_MONTH_DAY,
      }),
    data,
  );

  // Range charts can have multiple datums per timestamp, so derive the
  // bucket size from the number of unique x values (= band count) rather
  // than `data.length`. Caller may override (e.g. monthly interval where
  // each tick represents a single labeled day, not a bucket).
  const uniqueXCount = Object.keys(groupedByDate).length;
  const datumsPerColumn =
    datumsPerColumnProp ??
    (xAxisTicks?.length ? Math.round(uniqueXCount / xAxisTicks.length) : 1);

  return (
    <XYChart
      title={title}
      desc={description}
      data={data}
      accessors={accessors}
      margin={{
        left: leftMargin,
        bottom: bottomMargin,
      }}
      xScale={{
        type: 'band',
        paddingInner: barPadding,
        paddingOuter: barPadding / 2,
      }}
      yScale={{
        type: 'linear',
        domain: [0, yAxisMax],
      }}
    >
      <Grid
        numTicksRows={NUM_Y_TICKS}
        columnTickValues={xAxisTicks}
        centerColumns
        datumsPerColumn={datumsPerColumn}
      />
      <AxisLeft
        label={axisLeftLabel}
        labelOffset={yAxisLabelOffset}
        numTicks={NUM_Y_TICKS}
        hideTicks
        onWidthChange={(width) => setLeftMargin(width)}
      />
      <AxisBottom
        label={axisBottomLabel}
        tickValues={xAxisLabelTicks ?? xAxisTicks}
        tickFormat={xAxisTickFormat}
        onHeightChange={(height) => setBottomMargin(height)}
      />
      {Object.values(groupedByDate)
        // Remove days where datums are all null
        .filter(
          (datums) =>
            datums.filter((datum) => accessors.yAccessor(datum) !== null)
              .length > 0,
        )
        .map((datums) => (
          <TooltipMarker
            data={datums}
            accessors={accessors}
            ariaLabel={ariaDatumLabel(datums)}
          />
        ))}
      {data
        // Only render bars with values.
        .filter((datum) => accessors.yAccessor(datum) !== null)
        .map((datum) => {
          const shape = getDatumSymbol(datum);

          return (
            <RangeBar
              key={`bar-${accessors.xAccessor(datum)}:${accessors.yAccessor(
                datum,
              )}`}
              datum={datum}
              shape={shape}
              accessors={accessors as RangeBarProps<Datum, string>['accessors']}
            />
          );
        })}
      <Tooltip<Datum[]> snapToDatum={false}>{renderTooltip}</Tooltip>
    </XYChart>
  );
}
